import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { activityApi } from '../../utils/api/DocumentEditorApi/ActivityApi';
import { documentApi } from '../../utils/api/DocumentEditorApi/DocumentApi';
import { classApi } from '../../utils/api/ClassApi';
import { useClass } from '../../utils/ClassContext';
import { apiCall } from '../../utils/api/ApiUtils';
import {
  MdRefresh,
  MdFilterList,
  MdPerson,
  MdDescription,
  MdCode,
  MdQuestionAnswer,
  MdOpenInNew,
  MdClose,
  MdPlayArrow,
  MdEdit,
  MdCheckCircle,
  MdSchedule,
  MdAccountTree,
  MdSchema,
  MdVisibility,
  MdHelp,
  MdStar,
  MdError,
  MdMemory,
  MdDeleteForever,
  MdExpandMore,
  MdExpandLess,
} from 'react-icons/md';

const ActivityViewer = ({ username, userRole, apiBaseUrl }) => {
  const { selectedClass } = useClass();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // View mode: 'student' or 'document'
  const [viewMode, setViewMode] = useState('student');

  // Student sub-tab: 'activity' | 'memory'
  const [studentTab, setStudentTab] = useState('activity');

  // Student view state
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentSearchInput, setStudentSearchInput] = useState('');
  const [showStudentDropdown, setShowStudentDropdown] = useState(false);
  const [studentActivities, setStudentActivities] = useState([]);
  const [studentSummary, setStudentSummary] = useState(null);
  const dropdownRef = useRef(null);

  // Scaffold memory state
  const [memoryRecords, setMemoryRecords] = useState([]);
  const [memoryLoading, setMemoryLoading] = useState(false);
  const [memoryError, setMemoryError] = useState(null);
  const [expandedMemory, setExpandedMemory] = useState({});
  const [clearingMemory, setClearingMemory] = useState(false);

  // Document view state
  const [documents, setDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [documentActivities, setDocumentActivities] = useState([]);

  // Filters
  const [activityTypeFilter, setActivityTypeFilter] = useState('');
  const [limit, setLimit] = useState(50);

  // Memoize classContext to prevent infinite loops
  const classContext = useMemo(() => {
    if (!selectedClass) return null;
    return {
      class_id: selectedClass.class_id,
      class_code: selectedClass.class_code
    };
  }, [selectedClass?.class_id, selectedClass?.class_code]);

  // Scaffold memory helpers
  const fetchMemory = useCallback(async () => {
    if (!selectedStudent || !classContext?.class_id) return;
    setMemoryLoading(true);
    setMemoryError(null);
    try {
      const res = await apiCall(
        `/scaffold/memory/${selectedStudent}?class_id=${classContext.class_id}`,
        { method: 'GET' },
        classContext
      );
      if (res.success) {
        setMemoryRecords(res.records || []);
      } else {
        setMemoryError(res.error || 'Failed to load scaffold memory');
      }
    } catch (e) {
      setMemoryError(e.message || 'Failed to load scaffold memory');
    } finally {
      setMemoryLoading(false);
    }
  }, [selectedStudent, classContext]);

  const clearMemory = useCallback(async (moduleId = null, component = null) => {
    if (!selectedStudent || !classContext?.class_id) return;
    const label = moduleId ? `${moduleId}/${component || 'all'}` : 'all modules';
    if (!window.confirm(`Clear scaffold memory for ${selectedStudent} (${label})?`)) return;
    setClearingMemory(true);
    try {
      const res = await apiCall(
        `/scaffold/memory/${selectedStudent}`,
        {
          method: 'DELETE',
          body: JSON.stringify({
            class_id: classContext.class_id,
            module_id: moduleId || undefined,
            component: component || undefined,
          }),
        },
        classContext
      );
      if (res.success) {
        await fetchMemory();
      } else {
        setMemoryError(res.error || 'Failed to clear memory');
      }
    } catch (e) {
      setMemoryError(e.message || 'Failed to clear memory');
    } finally {
      setClearingMemory(false);
    }
  }, [selectedStudent, classContext, fetchMemory]);

  // Load memory when tab switches to 'memory' or student changes
  useEffect(() => {
    if (studentTab === 'memory' && selectedStudent && classContext?.class_id) {
      fetchMemory();
    }
  }, [studentTab, selectedStudent, classContext?.class_id, fetchMemory]);

  // Activity type labels
  const activityTypeLabels = {
    'document_open': 'Document Opened',
    'document_close': 'Document Closed',
    'document_session': 'Document Session',
    'code_edit': 'Code Edited',
    'code_run': 'Code Run',
    'erd_generated': 'ERD Generated',
    'erd_generation_failed': 'ERD Generation Failed',
    'erd_entity_modified': 'ERD Entity Modified',
    'erd_relationship_modified': 'ERD Relationship Modified',
    'erd_attribute_added': 'ERD Attribute Added',
    'erd_feedback_requested': 'ERD Feedback Requested',
    'erd_feedback_viewed': 'ERD Feedback Viewed',
    'erd_faq_opened': 'ERD FAQ Opened',
    'erd_feedback_rated': 'ERD Feedback Rated',
    'answer_mcq': 'MCQ Answered',
    'answer_short': 'Short Answer Submitted',
    'qa_question_open': 'Question Viewed',
    'qa_question_ask': 'Question Asked'
  };

  // Activity type icons
  const activityTypeIcons = {
    'document_open': MdOpenInNew,
    'document_close': MdClose,
    'document_session': MdSchedule,
    'code_edit': MdEdit,
    'code_run': MdPlayArrow,
    'erd_generated': MdPlayArrow,
    'erd_generation_failed': MdError,
    'erd_entity_modified': MdAccountTree,
    'erd_relationship_modified': MdSchema,
    'erd_attribute_added': MdEdit,
    'erd_feedback_requested': MdQuestionAnswer,
    'erd_feedback_viewed': MdVisibility,
    'erd_faq_opened': MdHelp,
    'erd_feedback_rated': MdStar,
    'answer_mcq': MdCheckCircle,
    'answer_short': MdCheckCircle,
    'qa_question_open': MdQuestionAnswer,
    'qa_question_ask': MdQuestionAnswer
  };

  // Load students list (for instructor view)
  useEffect(() => {
    let isMounted = true;

    const loadStudents = async () => {
      if (!selectedClass?.class_id || viewMode !== 'student') return;

      try {
        const response = await classApi.getClassEnrollments(selectedClass.class_id);
        console.log('Full API response:', response);
        if (isMounted && response.success) {
          // Extract usernames from enrollments - show all users (students, TAs, instructors)
          const enrolledUsers = response.enrollments
            .map(enrollment => ({
              username: enrollment.username,
              full_name: enrollment.full_name || enrollment.username,
              role_name: enrollment.role_name
            }))
            .sort((a, b) => a.username.localeCompare(b.username));
          setStudents(enrolledUsers);
        }
      } catch (err) {
        console.error('Failed to load students:', err);
      }
    };

    if (viewMode === 'student' && selectedClass?.class_id) {
      loadStudents();
    } else {
      setStudents([]);
    }

    return () => {
      isMounted = false;
    };
  }, [viewMode, selectedClass?.class_id]);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowStudentDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Filter students based on search input
  const filteredStudents = useMemo(() => {
    console.log('Students array:', students);
    console.log('Search input:', studentSearchInput);
    if (!studentSearchInput.trim()) return students;
    const searchLower = studentSearchInput.toLowerCase();
    const filtered = students.filter(student =>
      student.username.toLowerCase().includes(searchLower) ||
      student.full_name.toLowerCase().includes(searchLower)
    );
    console.log('Filtered students:', filtered);
    return filtered;
  }, [students, studentSearchInput]);

  // Load documents list - only when switching to document view
  useEffect(() => {
    let isMounted = true;
    
    const loadDocuments = async () => {
      if (!selectedClass?.class_id || viewMode !== 'document') return;
      
      const context = {
        class_id: selectedClass.class_id,
        class_code: selectedClass.class_code
      };
      
      try {
        setLoading(true);
        const response = await documentApi.getDocuments(null, context);
        if (isMounted && response.success) {
          setDocuments(response.documents || []);
        }
      } catch (err) {
        console.error('Failed to load documents:', err);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (viewMode === 'document' && selectedClass?.class_id) {
      loadDocuments();
    }

    return () => {
      isMounted = false;
    };
  }, [viewMode, selectedClass?.class_id]); // Only depend on class_id, not the whole object

  // Load student activities
  useEffect(() => {
    const loadStudentActivities = async () => {
      if (!selectedStudent || !classContext) return;

      try {
        setLoading(true);
        setError(null);
        
        const filters = {};
        if (activityTypeFilter) filters.activityType = activityTypeFilter;
        if (limit) filters.limit = limit;

        const response = await activityApi.getStudentActivities(
          selectedStudent,
          filters,
          classContext
        );

        if (response.success) {
          setStudentActivities(response.activities || []);
          setStudentSummary(response.summary || null);
        } else {
          setError(response.error || 'Failed to load activities');
        }
      } catch (err) {
        console.error('Failed to load student activities:', err);
        setError(err.message || 'Failed to load activities');
      } finally {
        setLoading(false);
      }
    };

    loadStudentActivities();
  }, [selectedStudent, activityTypeFilter, limit, classContext]);

  // Load document activities
  useEffect(() => {
    const loadDocumentActivities = async () => {
      if (!selectedDocument || !classContext) return;

      try {
        setLoading(true);
        setError(null);
        
        const filters = {};
        if (activityTypeFilter) filters.activityType = activityTypeFilter;

        const response = await activityApi.getDocumentActivities(
          selectedDocument,
          filters,
          classContext
        );

        if (response.success) {
          setDocumentActivities(response.activities || []);
        } else {
          setError(response.error || 'Failed to load activities');
        }
      } catch (err) {
        console.error('Failed to load document activities:', err);
        setError(err.message || 'Failed to load activities');
      } finally {
        setLoading(false);
      }
    };

    loadDocumentActivities();
  }, [selectedDocument, activityTypeFilter, classContext]);

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    if (seconds < 3600) return `${(seconds / 60).toFixed(1)}m`;
    return `${(seconds / 3600).toFixed(2)}h`;
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const handleRefresh = () => {
    if (viewMode === 'student' && selectedStudent) {
      // Trigger reload by temporarily clearing and re-setting
      const currentStudent = selectedStudent;
      setSelectedStudent(null);
      setTimeout(() => setSelectedStudent(currentStudent), 100);
    } else if (viewMode === 'document' && selectedDocument) {
      const currentDocument = selectedDocument;
      setSelectedDocument(null);
      setTimeout(() => setSelectedDocument(currentDocument), 100);
    }
  };

  return (
    <div className="h-full flex flex-col bg-gray-50 rounded-xl overflow-hidden">
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar */}
        <div className="w-64 bg-white border-r border-gray-200 overflow-y-auto">
          {/* View Mode Toggle */}
          <div className="p-3 border-b border-gray-200">
            <div className="flex items-center justify-between gap-2">
              {/* Toggle Switch */}
              <div className="relative inline-flex items-center bg-gray-200 rounded-full p-0.5" style={{ width: '100px', height: '26px' }}>
                {/* Sliding background */}
                <div
                  className="absolute top-0.5 bottom-0.5 rounded-full bg-blue-600 transition-all duration-300 ease-in-out"
                  style={{
                    width: '47px',
                    left: viewMode === 'student' ? '2px' : '51px'
                  }}
                />
                {/* Student Button */}
                <button
                  onClick={() => {
                    setViewMode('student');
                    setSelectedStudent(null);
                    setSelectedDocument(null);
                    setStudentSearchInput('');
                    setShowStudentDropdown(false);
                  }}
                  className={`relative z-10 flex-1 h-full flex items-center justify-center rounded-full text-xs font-medium transition-colors duration-300 ${
                    viewMode === 'student'
                      ? 'text-white'
                      : 'text-gray-600'
                  }`}
                >
                  <MdPerson className="text-sm" />
                </button>
                {/* Document Button */}
                <button
                  onClick={() => {
                    setViewMode('document');
                    setSelectedStudent(null);
                    setSelectedDocument(null);
                    setStudentSearchInput('');
                    setShowStudentDropdown(false);
                  }}
                  className={`relative z-10 flex-1 h-full flex items-center justify-center rounded-full text-xs font-medium transition-colors duration-300 ${
                    viewMode === 'document'
                      ? 'text-white'
                      : 'text-gray-600'
                  }`}
                >
                  <MdDescription className="text-sm" />
                </button>
              </div>
              
              {/* Refresh Button - Circular */}
              <button
                onClick={handleRefresh}
                className="w-6 h-6 flex items-center justify-center rounded-full bg-white text-gray-700 hover:bg-gray-50 transition-colors flex-shrink-0"
                title="Refresh"
              >
                <MdRefresh className="text-xs" />
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center gap-2 mb-3">
              <MdFilterList className="text-gray-600" />
              <span className="font-medium text-gray-700">Filters</span>
            </div>
            
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Activity Type
                </label>
                <select
                  value={activityTypeFilter}
                  onChange={(e) => setActivityTypeFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="">All Types</option>
                  {Object.entries(activityTypeLabels).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              {viewMode === 'student' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Limit
                  </label>
                  <input
                    type="number"
                    value={limit}
                    onChange={(e) => setLimit(parseInt(e.target.value) || 50)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    min="1"
                    max="500"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Selection List */}
          <div className="p-4">
            {viewMode === 'student' ? (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <MdPerson className="text-gray-600" />
                  <span className="font-medium text-gray-700">Select Student</span>
                </div>
                {selectedStudent ? (
                  <div className="mb-2">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between">
                      <span className="text-sm font-medium text-blue-900">{selectedStudent}</span>
                      <button
                        onClick={() => {
                          setSelectedStudent(null);
                          setStudentSearchInput('');
                        }}
                        className="text-blue-600 hover:text-blue-800"
                        title="Clear selection"
                      >
                        <MdClose />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="relative" ref={dropdownRef}>
                    <div className="text-sm text-gray-500 mb-2">
                      Search for a user:
                    </div>
                    <input
                      type="text"
                      value={studentSearchInput}
                      onChange={(e) => {
                        setStudentSearchInput(e.target.value);
                        setShowStudentDropdown(true);
                      }}
                      onFocus={() => setShowStudentDropdown(true)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && filteredStudents.length > 0) {
                          setSelectedStudent(filteredStudents[0].username);
                          setStudentSearchInput('');
                          setShowStudentDropdown(false);
                        }
                      }}
                      placeholder="Type to search users..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />

                    {showStudentDropdown && filteredStudents.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {filteredStudents.map((student) => (
                          <button
                            key={student.username}
                            onClick={() => {
                              setSelectedStudent(student.username);
                              setStudentSearchInput('');
                              setShowStudentDropdown(false);
                            }}
                            className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm transition-colors border-b border-gray-100 last:border-b-0"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="font-medium text-gray-900">{student.full_name}</div>
                                <div className="text-xs text-gray-500">{student.username}</div>
                              </div>
                              {student.role_name && (
                                <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600 font-medium">
                                  {student.role_name}
                                </span>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}

                    {showStudentDropdown && studentSearchInput && filteredStudents.length === 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-3">
                        <div className="text-sm text-gray-500 text-center">
                          No users found matching "{studentSearchInput}"
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <MdDescription className="text-gray-600" />
                  <span className="font-medium text-gray-700">Select Document</span>
                </div>
                <div className="space-y-1">
                  {documents.map((doc) => (
                    <button
                      key={doc.document_id}
                      onClick={() => setSelectedDocument(doc.document_id)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                        selectedDocument === doc.document_id
                          ? 'bg-blue-100 text-blue-900 font-medium'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {doc.title || 'Untitled Document'}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading && (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-500">Loading activities...</div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <div className="text-red-800">{error}</div>
            </div>
          )}

          {!loading && !error && viewMode === 'student' && !selectedStudent && (
            <div className="flex items-center justify-center h-64">
              <div className="text-center text-gray-500">
                <MdPerson className="text-4xl mx-auto mb-2 text-gray-400" />
                <p>Enter a student username to view their activities</p>
              </div>
            </div>
          )}

          {!loading && !error && viewMode === 'document' && !selectedDocument && (
            <div className="flex items-center justify-center h-64">
              <div className="text-center text-gray-500">
                <MdDescription className="text-4xl mx-auto mb-2 text-gray-400" />
                <p>Select a document to view activities</p>
              </div>
            </div>
          )}

          {/* Student Activities View */}
          {!loading && !error && viewMode === 'student' && selectedStudent && (
            <div>
              {/* Sub-tab bar */}
              <div className="flex gap-1 mb-5 border-b border-gray-200">
                <button
                  onClick={() => setStudentTab('activity')}
                  className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                    studentTab === 'activity'
                      ? 'bg-white border border-b-white border-gray-200 text-blue-700 -mb-px'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <MdSchedule className="text-base" /> Activity
                </button>
                <button
                  onClick={() => setStudentTab('memory')}
                  className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                    studentTab === 'memory'
                      ? 'bg-white border border-b-white border-gray-200 text-blue-700 -mb-px'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <MdMemory className="text-base" /> Student Memory
                </button>
              </div>

              {/* ── Scaffold Memory tab ── */}
              {studentTab === 'memory' && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-base font-semibold text-gray-800">
                      Student memory for <span className="text-blue-700">{selectedStudent}</span>
                    </h2>
                    <div className="flex gap-2">
                      <button
                        onClick={fetchMemory}
                        disabled={memoryLoading}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700"
                      >
                        <MdRefresh className={memoryLoading ? 'animate-spin' : ''} /> Refresh
                      </button>
                      <button
                        onClick={() => clearMemory()}
                        disabled={clearingMemory || memoryRecords.length === 0}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 text-red-700 disabled:opacity-40"
                      >
                        <MdDeleteForever /> Clear All
                      </button>
                    </div>
                  </div>

                  {memoryError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-sm text-red-700">
                      {memoryError}
                    </div>
                  )}

                  {memoryLoading && (
                    <div className="text-center text-gray-500 py-12">Loading scaffold memory…</div>
                  )}

                  {!memoryLoading && memoryRecords.length === 0 && (
                    <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
                      No scaffold memory found for this student.
                    </div>
                  )}

                  {!memoryLoading && memoryRecords.map((rec, idx) => {
                    const key = `${rec.module_id}::${rec.component}`;
                    const expanded = expandedMemory[key] ?? false;
                    return (
                      <div key={idx} className="bg-white rounded-lg border border-gray-200 mb-3 overflow-hidden">
                        {/* Record header */}
                        <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
                          <div className="flex items-center gap-3">
                            <MdMemory className="text-indigo-500 text-lg" />
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {rec.module_id}
                                <span className="ml-2 text-xs text-gray-500">({rec.component})</span>
                              </div>
                              <div className="text-xs text-gray-500">
                                {rec.stage_events?.length || 0} stage event(s) ·
                                {' '}{rec.selected_question_history?.length || 0} seen question(s) ·
                                last updated {rec.updated_at ? new Date(rec.updated_at).toLocaleString() : 'N/A'}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => clearMemory(rec.module_id, rec.component)}
                              disabled={clearingMemory}
                              className="flex items-center gap-1 px-2 py-1 text-xs bg-red-50 border border-red-200 rounded hover:bg-red-100 text-red-600 disabled:opacity-40"
                              title="Clear this record"
                            >
                              <MdDeleteForever /> Clear
                            </button>
                            <button
                              onClick={() => setExpandedMemory(prev => ({ ...prev, [key]: !expanded }))}
                              className="p-1 text-gray-500 hover:text-gray-700"
                            >
                              {expanded ? <MdExpandLess /> : <MdExpandMore />}
                            </button>
                          </div>
                        </div>

                        {/* Expanded: stage events */}
                        {expanded && (
                          <div className="p-4">
                            <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                              Stage Events
                            </div>
                            {(!rec.stage_events || rec.stage_events.length === 0) ? (
                              <p className="text-sm text-gray-400 italic">No stage events yet.</p>
                            ) : (
                              <div className="space-y-2">
                                {rec.stage_events.map((ev, ei) => (
                                  <div key={ei} className="bg-gray-50 rounded border border-gray-100 p-3 text-xs">
                                    <div className="flex items-center gap-3 mb-1">
                                      <span className="font-semibold text-indigo-700">{ev.stage_type}</span>
                                      <span className="text-gray-500">{ev.timestamp ? new Date(ev.timestamp).toLocaleString() : ''}</span>
                                      {ev.feedback_id && (
                                        <span className="text-gray-400">fb: {ev.feedback_id.slice(0, 8)}…</span>
                                      )}
                                    </div>
                                    {ev.selected_question_ids?.length > 0 && (
                                      <div className="text-gray-600">
                                        Questions: {ev.selected_question_ids.join(', ')}
                                      </div>
                                    )}
                                    {ev.correctness && Object.keys(ev.correctness).length > 0 && (
                                      <div className="flex flex-wrap gap-1 mt-1">
                                        {Object.entries(ev.correctness).map(([qid, correct]) => (
                                          <span
                                            key={qid}
                                            className={`px-1.5 py-0.5 rounded ${correct ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}
                                          >
                                            {qid.slice(-6)}: {correct ? '✓' : '✗'}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}

                            <div className="mt-3 text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
                              Seen Question IDs
                            </div>
                            {rec.selected_question_history?.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {rec.selected_question_history.map(qid => (
                                  <span key={qid} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">{qid}</span>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-gray-400 italic">None.</p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* ── Activity tab ── */}
              {studentTab === 'activity' && (
              <div>
              {/* Summary Card */}
              {studentSummary && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">
                    Activity Summary for {selectedStudent}
                  </h2>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <div className="text-sm text-gray-600">Total Activities</div>
                      <div className="text-2xl font-bold text-blue-900">
                        {studentSummary.total_activities}
                      </div>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4">
                      <div className="text-sm text-gray-600">Total Score</div>
                      <div className="text-2xl font-bold text-green-900">
                        {studentSummary.total_score.toFixed(1)}
                      </div>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4">
                      <div className="text-sm text-gray-600">Sessions</div>
                      <div className="text-2xl font-bold text-purple-900">
                        {studentSummary.document_sessions}
                      </div>
                    </div>
                    <div className="bg-orange-50 rounded-lg p-4">
                      <div className="text-sm text-gray-600">Time Spent</div>
                      <div className="text-2xl font-bold text-orange-900">
                        {formatDuration(studentSummary.total_session_time_seconds)}
                      </div>
                    </div>
                  </div>
                  
                  {studentSummary.snippet_usage && Object.keys(studentSummary.snippet_usage).length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="text-sm font-medium text-gray-700 mb-2">Code Snippet Usage:</div>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(studentSummary.snippet_usage).map(([snippetId, count]) => (
                          <div key={snippetId} className="bg-gray-100 rounded-lg px-3 py-1 text-sm">
                            <MdCode className="inline mr-1" />
                            {snippetId}: {count} runs
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Activities List */}
              <div className="space-y-3">
                {studentActivities.length === 0 ? (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center text-gray-500">
                    No activities found for this student
                  </div>
                ) : (
                  studentActivities.map((activity) => {
                    const Icon = activityTypeIcons[activity.activity_type] || MdDescription;
                    return (
                      <div
                        key={activity.activity_id}
                        className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3 flex-1">
                            <div className="mt-1">
                              <Icon className="text-xl text-blue-600" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium text-gray-900">
                                  {activityTypeLabels[activity.activity_type] || activity.activity_type}
                                </span>
                                {activity.score !== null && activity.score !== undefined && (
                                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                                    Score: {activity.score}
                                  </span>
                                )}
                                {activity.duration_seconds && (
                                  <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">
                                    {formatDuration(activity.duration_seconds)}
                                  </span>
                                )}
                              </div>
                              <div className="text-sm text-gray-600 mb-2">
                                {activity.description}
                              </div>
                              <div className="text-xs text-gray-500">
                                Document: {activity.document_id}
                              </div>
                              <div className="text-xs text-gray-500">
                                {formatTimestamp(activity.timestamp)}
                              </div>
                              {activity.metadata && Object.keys(activity.metadata).length > 0 && (
                                <details className="mt-2">
                                  <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
                                    View Details
                                  </summary>
                                  <pre className="mt-2 text-xs bg-gray-50 p-2 rounded overflow-x-auto whitespace-pre-wrap break-words max-w-full">
                                    {JSON.stringify(activity.metadata, null, 2)}
                                  </pre>
                                </details>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
            )}  {/* end activity tab */}
            </div>
          )}

          {/* Document Activities View */}
          {!loading && !error && viewMode === 'document' && selectedDocument && (
            <div>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  Activities for Document
                </h2>
                <p className="text-sm text-gray-600">
                  {documents.find(d => d.document_id === selectedDocument)?.title || selectedDocument}
                </p>
              </div>

              <div className="space-y-3">
                {documentActivities.length === 0 ? (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center text-gray-500">
                    No activities found for this document
                  </div>
                ) : (
                  documentActivities.map((activity) => {
                    const Icon = activityTypeIcons[activity.activity_type] || MdDescription;
                    return (
                      <div
                        key={activity.activity_id}
                        className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3 flex-1">
                            <div className="mt-1">
                              <Icon className="text-xl text-blue-600" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium text-gray-900">
                                  {activityTypeLabels[activity.activity_type] || activity.activity_type}
                                </span>
                                <span className="text-xs text-gray-600">
                                  by {activity.student_username}
                                </span>
                                {activity.score !== null && activity.score !== undefined && (
                                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                                    Score: {activity.score}
                                  </span>
                                )}
                                {activity.duration_seconds && (
                                  <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">
                                    {formatDuration(activity.duration_seconds)}
                                  </span>
                                )}
                              </div>
                              <div className="text-sm text-gray-600 mb-2">
                                {activity.description}
                              </div>
                              <div className="text-xs text-gray-500">
                                {formatTimestamp(activity.timestamp)}
                              </div>
                              {activity.metadata && Object.keys(activity.metadata).length > 0 && (
                                <details className="mt-2">
                                  <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
                                    View Details
                                  </summary>
                                  <pre className="mt-2 text-xs bg-gray-50 p-2 rounded overflow-x-auto whitespace-pre-wrap break-words max-w-full">
                                    {JSON.stringify(activity.metadata, null, 2)}
                                  </pre>
                                </details>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActivityViewer;

